





















































































import { Interaction } from '@/shared/interaction';
import { AppointmentCancellationType } from '@/shared/types/appointment-cancellation-type';
import { AppointmentType } from '@/shared/types/appointment-type';
import { InteractionLetterType } from '@/shared/types/interaction-letter-type';
import { InteractionOutcomeType } from '@/shared/types/interaction-outcome-type';
import { InteractionType } from '@/shared/types/interaction-type';
import { User } from '@/shared/user';
import { formatDate, formatDateAsString } from '@/shared/utilities/date-utilities';
import { DateTime } from 'luxon';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const typesModule = namespace('types');
const usersModule = namespace('users');

@Component
export default class ViewInteractionDialog extends Vue {
    @Prop({ required: true }) private interaction!: Interaction;
    @Prop({ required: true }) private visible!: boolean;

    @typesModule.Getter('appointmentCancellationTypes') private appointmentCancellationTypes!: Array<AppointmentCancellationType>;
    @typesModule.Getter('appointmentTypes') private appointmentTypes!: Array<AppointmentType>;
    @typesModule.Getter('interactionLetterTypes') private interactionLetterTypes!: Array<InteractionLetterType>;
    @typesModule.Getter('interactionOutcomeTypes') private interactionOutcomeTypes!: Array<InteractionOutcomeType>;
    @typesModule.Getter('interactionTypes') private interactionTypes!: Array<InteractionType>;

    @usersModule.Getter('users') private users!: Array<User>;

    private isVisible: boolean = this.visible;

    private get interactionTypeDescription(): string {
        if (this.interaction.interactionTypeId == null) {
            return '';
        }

        const interactionType = this.interactionTypes.find((type) => type.id === this.interaction.interactionTypeId);
        return interactionType == null ? '' : interactionType.value;
    }

    private get interactionOutcomeTypeDescription(): string {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return '';
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);
        return interactionOutcomeType == null ? '' : interactionOutcomeType.value;
    }

    private get appointmentDescription(): string {
        if (this.interaction.originalAppointment == null) {
            return '';
        }

        const appointmentType = this.appointmentTypes.find((type) => type.id === this.interaction.originalAppointment!.appointmentTypeId);
        const advisor = this.users.find((user) => user.id === this.interaction.originalAppointment!.legalAdvisorId);
        const advisorName = advisor == null ? '' : `${advisor.firstName.trim()} ${advisor.lastName.trim()}`;
        const appointmentDate = formatDateAsString(this.interaction.originalAppointment!.date!, DateTime.DATETIME_SHORT);

        return `${appointmentType == null ? '' : appointmentType.value} appointment with ${advisorName} on ${appointmentDate}`;
    }

    private get appointmentCancellationTypeDescription(): string {
        if (this.interaction.originalAppointment == null || this.interaction.originalAppointment.cancellationTypeId == null) {
            return '';
        }

        const appointmentCancellationType = this.appointmentCancellationTypes.find((type) => type.id === this.interaction.originalAppointment!.cancellationTypeId);
        return appointmentCancellationType == null ? '' : appointmentCancellationType.value;
    }

    private get interactionLetterTypeDescription(): string {
        if (this.interaction.letterTypeId == null) {
            return '';
        }

        const interactionLetterType = this.interactionLetterTypes.find((type) => type.id === this.interaction.letterTypeId);
        return interactionLetterType == null ? '' : interactionLetterType.value;
    }

    private get callbackDate(): string | Date | null {
        return formatDate(this.interaction.callbackDate);
    }

    private get showFutureContactConfirmation(): boolean {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return false;
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);
        return interactionOutcomeType == null ? false : interactionOutcomeType.isWillVoid || interactionOutcomeType.isWillReleased;
    }

    private get showAppointment(): boolean {
        if (this.interaction.interactionTypeId == null || this.interaction.interactionOutcomeTypeId == null) {
            return false;
        }

        const interactionType = this.interactionTypes.find((type) => type.id === this.interaction.interactionTypeId);
        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);

        return interactionType == null || interactionOutcomeType == null ? false : interactionType.isLFTSY || interactionOutcomeType.isAppointmentRebooked || interactionOutcomeType.isAppointmentCancelled;
    }

    private get showAppointmentCancellation(): boolean {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return false;
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);
        return interactionOutcomeType == null ? false : interactionOutcomeType.isAppointmentCancelled;
    }

    private get showCallback(): boolean {
        if (this.interaction.interactionOutcomeTypeId == null) {
            return false;
        }

        const interactionOutcomeType = this.interactionOutcomeTypes.find((type) => type.id === this.interaction.interactionOutcomeTypeId);
        return interactionOutcomeType == null ? false : interactionOutcomeType.isCallback || (interactionOutcomeType.isAppointmentCancelled && this.interaction.callbackDate != null);
    }

    private get formatClientAndPartnerNames(): string {
        if (this.interaction == null || this.interaction.clients == null || !this.interaction.clients.length) {
            return '';
        }

        const clientFirstName = this.interaction.clients[0].firstName != null ? this.interaction.clients[0].firstName.trim() : '';
        const clientLastName = this.interaction.clients[0].lastName != null ? this.interaction.clients[0].lastName.trim() : '';

        if (this.interaction.clients.length < 2) {
            return `${clientFirstName} ${clientLastName}`;
        }

        const partnerFirstName = this.interaction.clients[1].firstName != null ? this.interaction.clients[1].firstName.trim() : '';
        const partnerLastName = this.interaction.clients[1].lastName != null ? this.interaction.clients[1].lastName.trim() : '';

        return clientLastName.toLowerCase() === partnerLastName.toLowerCase()
            ? `${clientFirstName} and ${partnerFirstName} ${clientLastName}`.trim()
            : `${clientFirstName} ${clientLastName} and ${partnerFirstName} ${partnerLastName}`.trim();
    }

    @Watch('visible')
    private onVisibleChanged(visible: boolean): void {
        this.isVisible = visible;
    }

    private onCancel(): void {
        this.$emit('cancel');
    }

    private formatUserName(id: string | null): string {
        if (id == null) {
            return 'Unknown';
        }

        const user = this.users.find((u) => u.id === id);
        return user == null ? id : `${user.firstName == null ? '' : user.firstName.trim()} ${user.lastName == null ? '' : user.lastName.trim()}`;
    }
}
